import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listOnboardingSteps,
  createOnboardingStep,
  updateOnboardingStep,
  reorderOnboardingSteps,
  softDeleteOnboardingStep,
  restoreOnboardingStep,
  type CreateOnboardingStepInput,
  type UpdateOnboardingStepInput,
  type OnboardingBranch,
} from '../services/api/onboardingSteps';

const QUERY_KEY = ['onboarding-steps'] as const;

/**
 * HR management hook for the onboarding step catalog (Phase 63, plan 04).
 *
 * Wraps the 63-02 HR API via TanStack Query. Returns the full grouped list
 * (incl. inactive steps) plus create/update/reorder/softDelete/restore
 * mutations that all invalidate the ['onboarding-steps'] key on success.
 */
export function useOnboardingSteps() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => listOnboardingSteps().then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateOnboardingStepInput) => createOnboardingStep(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateOnboardingStepInput }) =>
      updateOnboardingStep(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: ({ branch, stepIds }: { branch: OnboardingBranch; stepIds: number[] }) =>
      reorderOnboardingSteps(branch, stepIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const softDeleteMutation = useMutation({
    mutationFn: (id: number) => softDeleteOnboardingStep(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: number) => restoreOnboardingStep(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  return {
    ...query,
    createMutation,
    updateMutation,
    reorderMutation,
    softDeleteMutation,
    restoreMutation,
  };
}
